import { defineStore, acceptHMRUpdate } from 'pinia';
import * as api from '@/api';
import { useShowStore } from '@/stores/shows';
import {
  doLoading,
  addUpdateRecord,
  buildChanges,
  clearEmpty,
  isExisting,
  removeRecord,
} from '@/utilities/helpers';
import { UploadManager } from '@/utilities/upload_manager';
import { urlsExpired } from '@/utilities/helpers';

export const useShowDocumentStore = defineStore('showDocuments', {
  state: () => ({
    /** @type {number} */
    loading: 0,
    /** @type {{id: string}[]} */
    records: [],
  }),
  getters: {
    count: (state) => state.records.length,
    retrieve: (state) => (showDocumentId) => state.records.find((v) => v.id === showDocumentId),
    isLoading: (state) => state.loading > 0,
    forShowId: (state) => (showId, includeRole = false) => state.records.filter((v) => v.showId === showId && (includeRole ? true : !v.showRoleId)),
    forShowRoleId: (state) => (showRoleId) => state.records.filter((v) => v.showRoleId === showRoleId),
  },
  actions: {
    async processRecord(record) {
      if (record.show) {
        await useShowStore().processRecord(record.show);
      }
      return addUpdateRecord(this.records, record, {
        skip: ['show'],
      });
    },
    async processRecords(records) {
      if (records) {
        for (const record of records) {
          await this.processRecord(record);
        }
      }
    },
    async removeRecord(showDocumentId) {
      removeRecord(this.records, showDocumentId);
    },
    async removeRecordsBy({ showId }) {
      if (showId) {
        const toRemove = this.forShowId(showId).map((e) => e.id);
        for (const toRemoveId of toRemove) {
          await this.removeRecord(toRemoveId);
        }
      }
    },
    /**
     * @param {string} showDocumentId
     * @param {boolean} force
     * @return {Promise<Object>}
     */
    async retrieveAsync(showDocumentId, {
      force = false,
    } = {}) {
      let record = this.records.find((v) => v.id === showDocumentId);
      if (!record || force) {
        await doLoading(this, async () => {
          const params = { showDocumentId };
          record = await api.showDocuments.retrieve(params);
          if (record) {
            record = await this.processRecord(record);
          }
        });
      }
      return record;
    },
    async reorder(showDocumentId, position, relatedShowDocumentId = null) {
      if (isExisting(showDocumentId)) {
        const payload = {
          showDocumentId,
          position,
        };
        if (relatedShowDocumentId) {
          payload.relatedShowDocumentId = relatedShowDocumentId;
        }
        return await doLoading(this, async () => {
          return await api.showDocuments.reorder(payload).then(async (data) => {
            await this.processRecords(data.updatedShowDocuments);
            return this.processRecord(data.showDocument);
          });
        });
      } else {
        throw new Error('Show role ID does not exist');
      }
    },
    async retrieveUrls(showDocumentId) {
      let record = this.records.find((v) => v.id === showDocumentId);
      await doLoading(this, async () => {
        if (!record) {
          record = await api.showDocuments.retrieve({ showDocumentId });
          record = await this.processRecord(record);
        } else {
          if (urlsExpired(record.urls)) {
            record = await api.showDocuments.retrieveUrls({ showDocumentId });
            record = await this.processRecord(record);
          }
        }
      });
      return record;
    },
    async startUpload({ showDocumentId, contentType, fileName }) {
      const initial = await api.showDocuments.upload({
        showDocumentId, upload: { start: { contentType, fileName } },
      });
      await this.processRecord(initial.showDocument);
      return new UploadManager(initial.presignedPost, async (completionToken) => {
        const data = await api.showDocuments.upload({ showDocumentId, upload: { finish: { completionToken } } });
        await this.processRecord(data.showDocument);
      });
    },
    async saveChanges(showDocumentId, updates, options = {}) {
      const payload = { showDocumentId, updates, options };

      return await doLoading(this, async () => {
        if (Object.keys(payload.updates).length > 0) {
          return await api.showDocuments.update(payload).then(async (data) => {
            return await this.processRecord(data.showDocument);
          });
        } else {
          return this.retrieve(showDocumentId);
        }
      });
    },
    async save(showDocument, extra = null, options = {}) {
      if (isExisting(showDocument.id)) {
        // existing
        const original = this.retrieve(showDocument.id);
        const changes = buildChanges(original, showDocument, {
          skip: ['id', 'updatedAt', 'urls'],
        });

        return this.saveChanges(showDocument.id, { ...changes, ...extra }, options);
      } else {
        // new showDocument
        const prepared = {
          showDocument: clearEmpty(showDocument, {
            skip: ['urls'],
          }),
          ...options,
        };
        return await doLoading(this, async () => {
          return await api.showDocuments.create(prepared).then(async (data) => {
            return this.processRecord(data.showDocument);
          });
        });
      }
    },
    async setActorId(showDocumentId, actorId, withOptions = {}) {
      const original = this.retrieve(showDocumentId);
      return await this.saveChanges(showDocumentId, buildChanges(original,
        { actorId: actorId },
        {
          only: ['actorId'],
        }), withOptions);
    },
    async delete(showDocumentId) {
      const showDocument = this.retrieve(showDocumentId);
      if (!showDocument) {
        return;
      }
      const payload = { showDocumentId };

      return await doLoading(this, async () => {
        return await api.showDocuments.delete(payload).then(async data => {
          await this.removeRecord(data.showDocumentId);
          return data.showDocumentId;
        });
      });
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useShowDocumentStore, import.meta.hot));
}
